<!-- Project images page — column-based, SERVER-DRIVEN data table (design: IMS Images Page).
     Case-information are now first-class image_instance columns returned inline as image.caseInfo;
     search / inline filters / the AND-OR Filters builder / sort / pagination all run server-side
     (the builder is translated to a parenthesized SQL predicate via the filterExpression param).
     Clicking Overview/Name opens the existing ImageDetailModal. -->
<template>
<div class="box error" v-if="!configUI['project-images-tab']">
  <h2> {{ $t('access-denied') }} </h2>
  <p>{{ $t('insufficient-permission') }}</p>
</div>
<div class="box error" v-else-if="error">
  <h2> {{ $t('error') }} </h2>
  <p>{{ $t('unexpected-error-info-message') }}</p>
</div>
<div v-else class="images-page">
  <b-loading :is-full-page="false" :active="loading" />

  <div v-if="!loading" class="it-card">
    <!-- ───────── toolbar ───────── -->
    <div class="it-toolbar">
      <div class="it-left">
        <div class="it-search">
          <i class="fas fa-search"></i>
          <input v-model="search" :placeholder="$t('search')" type="search" />
        </div>
        <button v-if="canAddImage" class="it-add" @click="addImageModal = true">
          <i class="fas fa-plus"></i> {{ $t('button-add-image') }}
        </button>
      </div>

      <div class="it-controls">
        <div class="it-anchor" v-click-outside="closeFiltersPanel">
          <button class="it-ctrl" :class="{active: openPanel === 'filters'}" @click="togglePanel('filters')">
            <i class="fas fa-sliders-h"></i> {{ $t('filters') }}
            <span v-if="activeFilterCount" class="it-count">{{ activeFilterCount }}</span>
          </button>

          <div v-if="openPanel === 'filters'" class="fb-pop">
            <image-filter-builder :value="filters" :groups="filterableGroups" @input="filters = $event" />
          </div>
        </div>

        <div class="it-anchor" v-click-outside="closeColumnsPanel">
          <button class="it-ctrl" :class="{active: openPanel === 'columns'}" @click="togglePanel('columns')">
            <i class="fas fa-columns"></i> {{ $t('columns') }}
          </button>

          <div v-if="openPanel === 'columns'" class="cm-pop">
            <div class="cm-search">
              <i class="fas fa-search"></i>
              <input v-model="columnSearch" :placeholder="$t('search')" />
            </div>
            <div class="cm-list">
              <div v-for="g in columnMenuGroups" :key="g.id">
                <div class="cm-group">{{ $t(g.labelKey) }}</div>
                <label
                  v-for="c in g.cols"
                  :key="c.key"
                  class="cm-item"
                  :class="{mandatory: c.mandatory}"
                  @click.prevent="toggleColumn(c)"
                >
                  <span class="cm-check" :class="{on: visible[c.key]}"><i v-if="visible[c.key]" class="fas fa-check"></i></span>
                  <span class="cm-name">{{ $t(c.labelKey) }}</span>
                  <span v-if="c.mandatory" class="cm-lock">{{ $t('required') }}</span>
                </label>
              </div>
            </div>
            <div class="cm-foot">
              <label class="cm-item" @click.prevent="toggleAllColumns">
                <span class="cm-check" :class="{on: allOptionalOn}"><i v-if="allOptionalOn" class="fas fa-check"></i></span>
                <span class="cm-name cm-strong">{{ $t('show-hide-all') }}</span>
              </label>
              <button class="cm-reset" @click="resetColumns">{{ $t('reset') }}</button>
            </div>
          </div>
        </div>

        <button class="it-ctrl" @click="onReset"><i class="fas fa-undo"></i> {{ $t('reset-filters') }}</button>
      </div>

      <div class="it-right">
        <button class="it-icon" :class="{on: inlineOn}" :title="$t('column-filters')" @click="inlineOn = !inlineOn">
          <i class="fas fa-filter"></i>
        </button>
        <button class="it-icon" :title="$t('refresh')" @click="onRefresh"><i class="fas fa-sync-alt"></i></button>
      </div>
    </div>

    <!-- ───────── table ───────── -->
    <div class="it-table-wrap">
      <b-loading :is-full-page="false" :active="loadingPage" />
      <table class="it-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="col.align === 'right' ? 'ta-right' : 'ta-left'"
              :style="{width: col.width ? col.width + 'px' : null, minWidth: col.minWidth ? col.minWidth + 'px' : null}"
            >
              <div class="it-th" :class="{end: col.align === 'right'}">
                <span>{{ $t(col.labelKey) }}</span>
                <button
                  v-if="col.sortable"
                  class="it-sort"
                  :class="{on: sort.key === col.key}"
                  :title="$t('sort')"
                  @click="onSort(col.key)"
                ><i class="fas" :class="sortIcon(col.key)"></i></button>
              </div>
            </th>
          </tr>
          <tr v-if="inlineOn" class="it-inline">
            <td v-for="col in columns" :key="col.key">
              <template v-if="col.filterable">
                <div class="it-inline-op">{{ (col.type === 'number' || col.type === 'date') ? $t('op-is') : $t('op-contains') }}</div>
                <input
                  :type="col.type === 'number' ? 'number' : (col.type === 'date' ? 'date' : 'text')"
                  :value="inlineFilters[col.key] || ''"
                  @input="e => setInline(col.key, e.target.value)"
                />
              </template>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length && !loadingPage">
            <td :colspan="columns.length" class="it-empty">{{ $t('no-image') }}</td>
          </tr>
          <tr v-for="row in rows" :key="row.id" class="it-row">
            <td v-for="col in columns" :key="col.key" :class="col.align === 'right' ? 'ta-right' : 'ta-left'">
              <a v-if="col.type === 'image'" class="it-thumb" :title="$t('overview')" @click="openDetail(row._image)">
                <image-thumbnail :image="row._image" :size="128" :key="`${row.id}-thumb`" :extra-parameters="tokenParam" />
              </a>
              <router-link v-else-if="col.key === 'name'" class="it-link" :to="`/project/${row._image.project}/image/${row.id}`">
                <image-name :image="row._image" showBothNames />
              </router-link>
              <router-link v-else-if="col.key === 'manualAnnotations'" class="it-link" :to="`/project/${row._image.project}/annotations?image=${row.id}&type=user`">
                {{ row.manualAnnotations }}
              </router-link>
              <router-link v-else-if="col.key === 'reviewedAnnotations'" class="it-link" :to="`/project/${row._image.project}/annotations?image=${row.id}&type=reviewed`">
                {{ row.reviewedAnnotations }}
              </router-link>
              <span v-else-if="col.type === 'date'">{{ fmtDate(row[col.key]) }}</span>
              <span v-else-if="isEmptyCell(row[col.key])" class="it-dash">—</span>
              <span v-else>{{ row[col.key] }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ───────── footer / pagination ───────── -->
    <div class="it-footer">
      <div class="it-perpage">
        <select v-model.number="perPage">
          <option v-for="n in [10, 25, 50]" :key="n" :value="n">{{ $t('count-per-page', {count: n}) }}</option>
        </select>
        <i class="fas fa-chevron-down"></i>
      </div>
      <div class="it-pager">
        <span class="it-total">{{ total }} {{ $tc('count-images', total) }}</span>
        <button class="it-pg" :disabled="currentPage <= 1" @click="currentPage--"><i class="fas fa-chevron-left"></i></button>
        <span class="it-pageinfo">{{ currentPage }} / {{ totalPages }}</span>
        <button class="it-pg" :disabled="currentPage >= totalPages" @click="currentPage++"><i class="fas fa-chevron-right"></i></button>
      </div>
    </div>
  </div>

  <add-image-modal :active.sync="addImageModal" @addImage="onRefresh" />
  <image-detail-modal :active.sync="detailModalActive" :image="detailImage" @refresh="onRefresh" />
</div>
</template>

<script>
import _ from 'lodash';
import {Ims, ImageInstance} from '@/api';
import {get} from '@/utils/store-helpers';
import {COLUMNS, COLUMN_GROUPS, initialVisible} from '@/utils/image-table-columns';
import {buildImageFilterExpression} from '@/utils/image-search';

import AddImageModal from '@/components/image/AddImageModal';
import ImageDetailModal from '@/components/image/ImageDetailModal';
import ImageName from '@/components/image/ImageName';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import ImageFilterBuilder from '@/components/image/ImageFilterBuilder';

// UI column key → server sort field (case columns sort by their own name)
const SORT_FIELD = {manualAnnotations: 'numberOfAnnotations', reviewedAnnotations: 'numberOfReviewedAnnotations', format: 'contentType'};

export default {
  name: 'list-images',
  components: {AddImageModal, ImageDetailModal, ImageName, ImageThumbnail, ImageFilterBuilder},
  data() {
    return {
      loading: true,
      loadingPage: false,
      error: false,
      rows: [],
      total: 0,

      search: '',
      openPanel: null, // 'filters' | 'columns' | null
      filters: [],
      visible: initialVisible(),
      columnSearch: '',
      inlineOn: false,
      inlineFilters: {},
      sort: {key: null, dir: 'asc'},
      perPage: 25,
      currentPage: 1,

      addImageModal: false,
      detailImage: null,
      detailModalActive: false,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    configUI: get('currentProject/configUI'),
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    blindMode() {
      return this.project.blindMode;
    },
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    canAddImage() {
      let isLSAAIUser = this.currentUser.username.endsWith('@lifescience-ri.eu');
      return !this.currentUser.guestByNow && (this.canManageProject || !this.project.isReadOnly) && !isLSAAIUser;
    },
    tokenParam() {
      return {authorization: 'Bearer ' + this.shortTermToken};
    },
    columns() {
      return COLUMNS.filter(c => this.visible[c.key]);
    },
    filterableGroups() {
      return COLUMN_GROUPS
        .map(g => ({...g, cols: COLUMNS.filter(c => c.group === g.id && c.filterable)}))
        .filter(g => g.cols.length);
    },
    columnMenuGroups() {
      const q = this.columnSearch.trim().toLowerCase();
      return COLUMN_GROUPS
        .map(g => ({
          ...g,
          cols: COLUMNS.filter(c => c.group === g.id && this.$t(c.labelKey).toLowerCase().includes(q))
        }))
        .filter(g => g.cols.length);
    },
    optionalKeys() {
      return COLUMNS.filter(c => !c.mandatory).map(c => c.key);
    },
    allOptionalOn() {
      return this.optionalKeys.every(k => this.visible[k]);
    },
    activeFilterCount() {
      return this.filters.filter(f => f.value !== '' || f.operator === 'is-empty').length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    },
  },
  watch: {
    search() {
      this.scheduleReload();
    },
    filters: {
      deep: true,
      handler() {
        this.scheduleReload();
      }
    },
    inlineFilters: {
      deep: true,
      handler() {
        this.scheduleReload();
      }
    },
    sort: {
      deep: true,
      handler() {
        this.reloadFirstPage();
      }
    },
    perPage() {
      this.reloadFirstPage();
    },
    currentPage() {
      this.fetchPage();
    },
  },
  methods: {
    sortIcon(key) {
      if (this.sort.key !== key) {
        return 'fa-sort';
      }
      return this.sort.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    },
    isEmptyCell(v) {
      return v === '' || v === null || v === undefined;
    },
    fmtDate(v) {
      if (!v) {
        return '';
      }
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
    },

    togglePanel(name) {
      this.openPanel = this.openPanel === name ? null : name;
    },
    closeFiltersPanel() {
      if (this.openPanel === 'filters') {
        this.openPanel = null;
      }
    },
    closeColumnsPanel() {
      if (this.openPanel === 'columns') {
        this.openPanel = null;
      }
    },

    toggleColumn(c) {
      if (c.mandatory) {
        return;
      }
      this.$set(this.visible, c.key, !this.visible[c.key]);
    },
    toggleAllColumns() {
      const on = !this.allOptionalOn;
      this.optionalKeys.forEach(k => this.$set(this.visible, k, on));
    },
    resetColumns() {
      this.visible = initialVisible();
    },

    setInline(key, value) {
      this.$set(this.inlineFilters, key, value);
    },

    onSort(key) {
      if (this.sort.key === key) {
        this.sort = {key, dir: this.sort.dir === 'asc' ? 'desc' : 'asc'};
      } else {
        this.sort = {key, dir: 'asc'};
      }
    },
    onReset() {
      this.filters = [];
      this.inlineFilters = {};
      this.search = '';
      this.sort = {key: null, dir: 'asc'};
      this.reloadFirstPage();
    },
    onRefresh() {
      this.fetchPage();
    },

    openDetail(image) {
      this.detailImage = image;
      this.detailModalActive = true;
    },

    // Build the combined server-side filter expression (left-folded, matching the builder):
    //   (search OR-group) AND inline-filters AND (builder rows with their own AND/OR).
    buildFilterExpression() {
      return buildImageFilterExpression({
        search: this.search,
        builderFilters: this.filters,
        inlineColumns: this.columns,
        inlineFilters: this.inlineFilters
      });
    },

    async fetchPage() {
      this.loadingPage = true;
      try {
        const expr = this.buildFilterExpression();
        const params = {
          sort: this.sort.key ? (SORT_FIELD[this.sort.key] || this.sort.key) : 'created',
          order: this.sort.key ? this.sort.dir : 'desc',
          offset: (this.currentPage - 1) * this.perPage,
          max: this.perPage,
        };
        if (expr.length) {
          params.filterExpression = JSON.stringify(expr);
        }
        const url = 'project/' + this.project.id + '/imageinstance.json';
        const data = (await Ims.instance.api.get(url, {params})).data;
        this.total = data.size != null ? data.size : (data.collection ? data.collection.length : 0);
        const blinded = this.blindMode && !this.canManageProject;
        this.rows = (data.collection || []).map(j => {
          const ci = j.caseInfo || {};
          const img = new ImageInstance(j);
          img.caseInfo = ci; // let the detail modal render case-info inline (no re-fetch)
          return {
            id: j.id,
            _image: img,
            name: blinded ? (j.blindedName || j.instanceFilename) : j.instanceFilename,
            magnification: j.magnification,
            manualAnnotations: j.numberOfAnnotations,
            reviewedAnnotations: j.numberOfReviewedAnnotations,
            format: j.contentType,
            ...ci,
          };
        });
      } catch (error) {
        console.log(error);
        this.error = true;
      }
      this.loadingPage = false;
    },
    reloadFirstPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1; // triggers the currentPage watcher → fetchPage
      } else {
        this.fetchPage();
      }
    },
    scheduleReload: _.debounce(function () {
      this.reloadFirstPage();
    }, 350),
  },
  async created() {
    try {
      await this.fetchPage();
    } catch (error) {
      console.log(error);
      this.error = true;
    }
    this.loading = false;
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/colors.scss';

$blue: $primary;
$ink: #1f2937;
$muted: #6b7280;
$muted-2: #9aa3b2;
$line: #e8eaef;

.images-page {
  position: relative;
  background: #fff;
  min-height: calc(100vh - 50px);
  padding: 12px 28px 40px;

  ::v-deep .fas { padding-right: 0; }
}

.it-card {
  border: 1px solid $line;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  /* visible so the Filters / Columns popovers can overflow the card */
  overflow: visible;
}
.it-table-wrap { border-radius: 0 0 16px 16px; }

/* toolbar */
.it-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border-bottom: 1px solid $line;
  flex-wrap: wrap;
}
.it-left { display: flex; align-items: center; gap: 12px; }
.it-search {
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid #e3e7ee;
  border-radius: 9px;
  padding: 8px 13px;
  width: 240px;
  color: $muted-2;
  input { border: 0; outline: 0; background: transparent; font-size: 14px; width: 100%; color: $ink; }
}
.it-add {
  display: flex;
  align-items: center;
  gap: 7px;
  background: $blue;
  color: #fff;
  border: 0;
  border-radius: 9px;
  padding: 9px 15px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: $dark-primary; }
}
.it-controls { display: flex; align-items: center; gap: 4px; margin-left: 6px; }
.it-anchor { position: relative; }
.it-ctrl {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 9px 13px;
  font-size: 14px;
  font-weight: 600;
  color: #3a4452;
  cursor: pointer;
  &:hover { background: #f4f6f9; }
  &.active { background: #eef1f6; color: $ink; }
}
.it-count {
  background: $blue;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  min-width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.it-right { display: flex; align-items: center; gap: 4px; margin-left: auto; }
.it-icon {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: $muted-2;
  font-size: 17px;
  &:hover { background: #f4f6f9; }
  &.on { color: $blue; }
}

/* filter builder popover */
.fb-pop {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 60;
  min-width: 720px;
  max-width: 92vw;
  padding: 18px 20px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid $line;
  box-shadow: 0 18px 48px rgba(31, 41, 55, 0.16);
}
.fb-empty { color: $muted; font-size: 14px; padding: 4px 2px 14px; }
.fb-row { display: flex; align-items: flex-end; gap: 14px; margin-bottom: 14px; }
.fb-x { background: transparent; border: 0; color: #8b94a3; cursor: pointer; padding: 4px; margin-bottom: 6px; }
.fb-x:hover { color: $danger-zaya; }
.fb-first { width: 30px; flex-shrink: 0; }
.fb-conj { position: relative; width: 70px; flex-shrink: 0; }
.fb-conj select {
  appearance: none; -webkit-appearance: none;
  border: 0; border-bottom: 1px solid #d7dbe3; background: transparent;
  font-size: 14px; font-weight: 600; color: #3a4452;
  padding: 8px 18px 8px 2px; width: 100%; cursor: pointer; outline: none;
}
.fb-conj-chev { position: absolute; right: 2px; bottom: 11px; color: $muted-2; pointer-events: none; font-size: 0.7rem; }
.fb-field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 120px; }
.fb-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8b94a3; }
.fb-select { position: relative; }
.fb-select select {
  appearance: none; -webkit-appearance: none;
  border: 0; border-bottom: 1px solid #d7dbe3; background: transparent;
  font-size: 14px; color: $ink; padding: 8px 24px 8px 2px; width: 100%; cursor: pointer; outline: none; font-weight: 600;
}
.fb-select-chev { position: absolute; right: 4px; top: 9px; color: $muted-2; pointer-events: none; font-size: 0.8rem; }
.fb-value { border: 0; border-bottom: 1px solid #d7dbe3; background: transparent; font-size: 14px; color: $ink; padding: 8px 2px; width: 100%; outline: none; }
.fb-value:focus { border-bottom-color: $blue; }
.fb-value-empty { color: $muted-2; }
.fb-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 6px; padding-top: 14px; border-top: 1px dashed $line; }
.fb-add {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid $line; border-radius: 10px;
  padding: 9px 15px; font-size: 14px; font-weight: 600; color: #3a4452; cursor: pointer;
  &:hover { background: #f8fafc; }
}
.fb-removeall {
  display: flex; align-items: center; gap: 8px;
  background: #27324a; border: 0; border-radius: 10px;
  padding: 9px 15px; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer;
}

/* columns checklist popover */
.cm-pop {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 60;
  width: 320px;
  padding: 14px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid $line;
  box-shadow: 0 18px 48px rgba(31, 41, 55, 0.16);
}
.cm-search {
  display: flex; align-items: center; gap: 9px;
  border: 1px solid #e3e7ee; border-radius: 10px; padding: 9px 12px; margin-bottom: 10px; color: $muted-2;
  input { border: 0; outline: 0; background: transparent; font-size: 14px; flex: 1; color: $ink; }
}
.cm-list { max-height: 360px; overflow-y: auto; padding-right: 4px; }
.cm-group { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: $muted-2; padding: 10px 4px 4px; }
.cm-item { display: flex; align-items: center; gap: 11px; padding: 8px 4px; font-size: 14px; color: $ink; cursor: pointer; }
.cm-item.mandatory { opacity: 0.7; cursor: default; }
.cm-name { flex: 1; }
.cm-strong { font-weight: 600; }
.cm-check {
  width: 20px; height: 20px; border-radius: 6px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid #c8cfda; color: #fff; font-size: 0.7rem;
}
.cm-check.on { background: #27324a; border-color: #27324a; }
.cm-lock { margin-left: auto; font-size: 10.5px; font-weight: 600; color: #b0a6b0; text-transform: uppercase; letter-spacing: 0.04em; }
.cm-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; padding-top: 12px; border-top: 1px solid $line; }
.cm-reset { background: transparent; border: 0; color: $muted-2; font-size: 14px; font-weight: 600; cursor: pointer; padding: 4px 6px; }

/* table */
.it-table-wrap { width: 100%; overflow-x: auto; position: relative; min-height: 8rem; }
.it-table { width: 100%; border-collapse: collapse; min-width: 640px; }
.it-table th {
  padding: 15px 18px; font-size: 14px; font-weight: 700; color: #374151;
  white-space: nowrap; border-bottom: 1px solid $line; background: #fff;
}
.it-th { display: flex; align-items: center; gap: 8px; }
.it-th.end { justify-content: flex-end; }
.it-sort { background: transparent; border: 0; cursor: pointer; color: #c2c8d2; padding: 0; font-size: 0.85rem; }
.it-sort.on { color: $blue; }
.it-table td { padding: 12px 18px; font-size: 14px; color: $ink; border-bottom: 1px solid $line; vertical-align: middle; }
.ta-right { text-align: right; }
.ta-left { text-align: left; }
.it-row:hover td { background: #f9fafb; }
.it-link { color: $blue; cursor: pointer; font-weight: 500; &:hover { text-decoration: underline; } }
.it-thumb { cursor: pointer; display: inline-block; ::v-deep .image-thumbnail { max-height: 3.4rem; max-width: 6.4rem; border-radius: 6px; } }
.it-dash { color: #c2c8d2; }
.it-empty { text-align: center; padding: 56px 0; color: $muted; font-size: 15px; }

/* inline filter row */
.it-inline td { padding: 8px 14px 12px; border-bottom: 1px solid $line; background: #fff; vertical-align: top; }
.it-inline-op { font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: $muted-2; margin-bottom: 2px; }
.it-inline input { border: 0; border-bottom: 1px solid #e1e5ec; background: transparent; font-size: 13px; padding: 5px 2px; width: 100%; outline: none; color: $ink; }
.it-inline input:focus { border-bottom-color: $blue; }

/* footer */
.it-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; }
.it-perpage { position: relative; }
.it-perpage select {
  appearance: none; -webkit-appearance: none;
  border: 1px solid #e3e7ee; border-radius: 9px; padding: 8px 32px 8px 13px;
  font-size: 14px; color: #3a4452; cursor: pointer; background: #fff; font-weight: 500;
}
.it-perpage .fas { position: absolute; right: 11px; top: 11px; color: $muted-2; pointer-events: none; font-size: 0.8rem; }
.it-pager { display: flex; align-items: center; gap: 12px; }
.it-total { font-size: 13px; color: $muted; }
.it-pageinfo { font-size: 13.5px; font-weight: 600; color: #3a4452; min-width: 54px; text-align: center; }
.it-pg {
  min-width: 36px; height: 36px; padding: 0 9px;
  border-radius: 9px; border: 1px solid $line; background: #f3f5f8; color: #5b6472;
  font-size: 14px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover:not(:disabled) { border-color: #cdd6e4; background: #fff; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}

@media screen and (max-width: 768px) {
  .images-page { padding: 10px 12px 30px; }
  .it-toolbar { gap: 10px; }
  .it-search { width: 100%; }
}
</style>
